import {
  EProductStatus,
  ICreateProduct,
  ICreateProductVariation,
  IProductAiGeneratedImage,
  IProductFormData,
  IProductVariationFormData,
  IUpdateProduct,
  IUpdateProductVariation
} from 'models/product'
import { normalizeNumber } from 'shared/utils/normalizeNumber'
import { NEW_PHOTO_TOKEN } from 'shared/constants/product'
import { getNormalizedVariations } from './variations'

// В photo уходят только новые файлы, поэтому порядок передаём отдельным списком:
// сохранённые снимки — своим ключом, новые — позицией в photo
export const getPhotoOrder = (photo: any[] = []) => {
  let uploadIndex = 0
  return photo.map((el: any) =>
    el?.originFileObj ? `${NEW_PHOTO_TOKEN}${uploadIndex++}` : el?.uid
  )
}

// Бэкенд отклоняет товар с повторяющимися названиями характеристик, а в форме одно
// имя может оказаться сразу в двух блоках (общем и обязательном) — оставляем первое
const normalizeCharacteristics = (items: Array<{ name: string; value: string }>) =>
  items
    .map((item) => ({
      ...item,
      name: item?.name?.trim(),
      value: item?.value?.trim()
    }))
    .filter((item) => Boolean(item.name) && Boolean(item.value))
    .filter(
      (item, index, list) =>
        list.findIndex(
          (current) =>
            current.name.toLowerCase() === item.name.toLowerCase()
        ) === index
    )

export const getCreateSubmitData = (
  data: IProductFormData,
  status:
    | EProductStatus.DRAFT
    | EProductStatus.PUBLISHED
    | EProductStatus.REJECTED
    | undefined,
  firstVariationName: string | undefined,
  secondVariationName: string | undefined
): ICreateProduct => {
  // Объединяем обычные характеристики с обязательными
  const allCharacteristics = normalizeCharacteristics([
    ...(data?.characteristics || []),
    ...(data?.optionalCharacteristics || []),
    ...(data?.requiredCharacteristics || [])
  ])

  return {
    name: data.name,
    status,
    category_id: data.categories_id?.[0],
    subCategory_id: data.categories_id?.[1],
    vendor_code: data.vendor_code,
    price: normalizeNumber(data.price),
    tax: data.tax,
    dimension: {
      width: normalizeNumber(data.width),
      length: normalizeNumber(data.length),
      height: normalizeNumber(data.height),
      weight: normalizeNumber(data.weight),
      unit: data.unit,
      in_stock: normalizeNumber(data.in_stock),
      on_demand: !!data.on_demand,
      auto_deduct_stock: data.auto_deduct_stock !== false
    },
    photo: (data.photo ?? []).map((el: any) => el.originFileObj),
    desc: data.desc,
    ...(allCharacteristics.length > 0 && {
      characteristic: allCharacteristics
    }),
    variations: getNormalizedVariations(data?.variations, firstVariationName, secondVariationName)
  }
}

export const getEditSubmitData = (
  data: IProductFormData,
  status:
    | EProductStatus.DRAFT
    | EProductStatus.PUBLISHED
    | EProductStatus.REJECTED
    | undefined,
  productId: string,
  toDeletePhotos: string[],
  toDeleteVariations: string[],
  firstVariationName: string | undefined,
  secondVariationName: string | undefined,
  generatedImage?: string,
  allowWebviewExperiment = false
): IUpdateProduct & IProductAiGeneratedImage => {
  // Объединяем обычные характеристики с обязательными
  const allCharacteristics = normalizeCharacteristics([
    ...(data?.characteristics || []),
    ...(data?.optionalCharacteristics || []),
    ...(data?.requiredCharacteristics || [])
  ])

  return {
    // EXPERIMENT: product-webview — sellers and variations never send the flag.
    ...(allowWebviewExperiment && { experimental_open_in_webview: data.experimental_open_in_webview === true }),
    id: productId,
    toDeletePhotos,
    toDeleteVariations,
    name: data.name,
    status,
    category_id: data.categories_id?.[0],
    subCategory_id: data.categories_id?.[1],
    vendor_code: data.vendor_code,
    price: normalizeNumber(data.price),
    tax: data.tax,
    dimension: {
      width: normalizeNumber(data.width),
      length: normalizeNumber(data.length),
      height: normalizeNumber(data.height),
      weight: normalizeNumber(data.weight),
      unit: data.unit,
      in_stock: normalizeNumber(data.in_stock),
      on_demand: !!data.on_demand,
      auto_deduct_stock: data.auto_deduct_stock !== false
    },
    photo: (data.photo ?? []).map((el: any) => el.originFileObj).filter((el) => el),
    photoOrder: getPhotoOrder(data.photo),
    generatedImage,
    desc: data.desc,
    ...(allCharacteristics.length > 0 && {
      characteristic: allCharacteristics
    }),
    variations: getNormalizedVariations(data?.variations, firstVariationName, secondVariationName)
  }
}

export const getCreateVariationSubmitData = (
  data: IProductVariationFormData,
  sellerId: string,
  parentProductId: string | undefined,
  status: EProductStatus.DRAFT | EProductStatus.PUBLISHED
): ICreateProductVariation => {
  // Объединяем обязательные и необязательные характеристики
  const allCharacteristics = normalizeCharacteristics([
    ...(data?.requiredCharacteristics || []),
    ...(data?.optionalCharacteristics || [])
  ])

  return {
    seller_id: sellerId,
    // при редактировании существующего товара родитель известен — бэкенд привяжет вариацию сразу
    ...(parentProductId && { parent_id: parentProductId }),
    status,
    name: data.name,
    vendor_code: data.vendor_code,
    price: normalizeNumber(data.price),
    tax: data.tax,
    dimension: {
      width: normalizeNumber(data.width),
      length: normalizeNumber(data.length),
      height: normalizeNumber(data.height),
      weight: normalizeNumber(data.weight),
      unit: data.unit,
      in_stock: normalizeNumber(data.in_stock),
      on_demand: !!data.on_demand,
      auto_deduct_stock: data.auto_deduct_stock !== false
    },
    desc: data?.desc,
    photo: data?.photo?.map((el: any) => el.originFileObj),
    ...(allCharacteristics.length > 0 && {
      characteristic: allCharacteristics
    })
  } as ICreateProductVariation
}

export const getEditVariationSubmitData = (
  data: IProductVariationFormData,
  productId: string,
  toDeletePhotos: string[],
  status?: EProductStatus.PUBLISHED
): IUpdateProductVariation => {
  // Объединяем обязательные и необязательные характеристики
  const allCharacteristics = normalizeCharacteristics([
    ...(data?.requiredCharacteristics || []),
    ...(data?.optionalCharacteristics || [])
  ])

  return {
    id: productId,
    toDeletePhotos,
    status,
    // type здесь не отправляем: тип VARIATION вместе с parent_id ставит только бэкенд
    // при привязке — досрочный type без родителя создавал вариации-«сироты»
    name: data.name,
    vendor_code: data.vendor_code,
    price: normalizeNumber(data.price),
    tax: data.tax,
    dimension: {
      width: normalizeNumber(data.width),
      length: normalizeNumber(data.length),
      height: normalizeNumber(data.height),
      weight: normalizeNumber(data.weight),
      unit: data.unit,
      in_stock: normalizeNumber(data.in_stock),
      on_demand: !!data.on_demand,
      auto_deduct_stock: data.auto_deduct_stock !== false
    },
    desc: data?.desc,
    photo: data?.photo?.map((el: any) => el.originFileObj).filter((el) => el),
    ...(allCharacteristics.length > 0 && {
      characteristic: allCharacteristics
    })
  } as IUpdateProductVariation
}
